import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { WhatsAppOtpLogin } from '../components/custom/WhatsAppLogin';
import { toast } from 'sonner';
import { Loader2, CreditCard, Banknote, ShieldCheck, Lock } from 'lucide-react';
import { FreeDeliveryPill } from '../components/custom/AnnouncementBar';
import api from '../utils/api';
import { trackCheckout, trackPurchase } from '../utils/analytics';

function CheckoutPage() {
  const { cart, cartTotal, user, clearCart, isLoaded } = useCart();
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('online'); // 'online' or 'cod'
  const [codOtpStep, setCodOtpStep] = useState('idle'); // 'idle', 'sending', 'verify', 'auto_verified'
  const [codOtp, setCodOtp] = useState('');
  const [codOtpSending, setCodOtpSending] = useState(false);
  const [devOtp, setDevOtp] = useState('');
  
  // Check if user is already verified via WhatsApp login (skip COD OTP)
  const isUserVerified = !!user;
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  
  const [address, setAddress] = useState({
    name: '',
    phone: user?.phone || '',
    email: '',
    line1: '',
    line2: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
  });

  // Auto-fill phone from WhatsApp login when user logs in
  useEffect(() => {
    if (user?.phone && !address.phone) {
      setAddress(prev => ({ ...prev, phone: user.phone }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Calculate final total
  const finalTotal = cartTotal - discountAmount;

  // Redirect to cart if empty - only after cart is loaded
  useEffect(() => {
    if (isLoaded && cart.length === 0) {
      navigate('/cart');
    }
  }, [cart.length, navigate, isLoaded]);

  const handleLoginSuccess = (userData) => {
    setAddress(prev => ({ ...prev, phone: userData.phone }));
  };

  // Apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    if (!user) {
      toast.error('Please login first to apply coupon');
      setShowLogin(true);
      return;
    }

    setCouponLoading(true);
    try {
      const result = await api.applyCoupon(couponCode, cartTotal, user.phone);
      
      if (result.success) {
        setAppliedCoupon(result.coupon);
        setDiscountAmount(result.discount_amount);
        toast.success(`Coupon applied! You save ₹${result.discount_amount}`);
      } else {
        toast.error(result.detail || 'Invalid coupon');
      }
    } catch (error) {
      toast.error('Failed to apply coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  // Send OTP for COD verification
  const handleSendCodOtp = async () => {
    if (!address.phone || address.phone.length < 10) {
      toast.error('Please enter a valid phone number first');
      return;
    }

    setCodOtpSending(true);
    try {
      const formattedPhone = address.phone.startsWith('+91') ? address.phone : `+91${address.phone}`;
      const result = await api.sendOTP(formattedPhone);
      
      if (result.success) {
        toast.success('OTP sent to your WhatsApp for COD verification!');
        if (result.otp_dev) {
          setDevOtp(result.otp_dev);
        }
        setCodOtpStep('verify');
      } else {
        toast.error(result.message || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setCodOtpSending(false);
    }
  };

  const validateForm = () => {
    if (!user) {
      setShowLogin(true);
      return false;
    }

    if (!address.name || !address.phone || !address.line1 || !address.line2 || !address.city || !address.state || !address.pincode) {
      toast.error('Please fill all required address fields');
      return false;
    }

    // Skip OTP check for COD if user is already verified via WhatsApp login
    if (paymentMethod === 'cod' && !isUserVerified && codOtp.length !== 6) {
      toast.error('Please verify OTP for Cash on Delivery');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    
    // Track Facebook Pixel InitiateCheckout event
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_ids: cart.map(item => item.id),
        contents: cart.map(item => ({
          id: item.id,
          quantity: item.quantity,
        })),
        value: cartTotal,
        currency: 'INR',
        num_items: cart.length,
      });
    }
    
    // Track checkout in our analytics
    trackCheckout(finalTotal);
    
    try {
      const formattedPhone = address.phone.startsWith('+91') ? address.phone : `+91${address.phone}`;
      
      const orderData = {
        phone: formattedPhone,
        items: cart.map(item => ({
          product_id: item.id,
          title: item.title,
          price: item.price,
          quantity: item.quantity,
          image: item.image_urls?.[0] || null, // Include product image
        })),
        total_amount: finalTotal,
        delivery_address: { ...address, phone: formattedPhone },
        payment_method: paymentMethod,
        // For COD: send 'auto_verified' if user is already logged in via WhatsApp
        cod_otp: paymentMethod === 'cod' ? (isUserVerified ? 'auto_verified' : codOtp) : null,
        coupon_code: appliedCoupon?.code || null,
        discount_amount: discountAmount,
      };

      const result = await api.createOrder(orderData);

      // Handle COD order success
      if (paymentMethod === 'cod') {
        if (result.success) {
          // Track Facebook Pixel Purchase event
          if (window.fbq) {
            window.fbq('track', 'Purchase', {
              content_ids: cart.map(item => item.id),
              contents: cart.map(item => ({
                id: item.id,
                quantity: item.quantity,
              })),
              value: finalTotal,
              currency: 'INR',
              num_items: cart.length,
            });
          }
          
          // Track purchase in our analytics - AWAIT to ensure it completes before redirect
          try {
            await trackPurchase(result.order_id, finalTotal);
          } catch (e) {
            console.log('Analytics tracking error:', e);
          }
          
          // Clear cart and navigate to success page (same as Razorpay flow)
          localStorage.removeItem('tiffin_cart');
          window.location.href = `/order-success?order_id=${result.order_id}&method=cod&total=${finalTotal}`;
        } else {
          toast.error(result.detail || 'Failed to place order. Please try again.');
        }
        return;
      }

      // Handle online payment (Razorpay)
      const orderIdForSuccess = result.order_id;
      const options = {
        key: result.razorpay_key_id,
        amount: result.amount,
        currency: result.currency,
        order_id: result.razorpay_order_id,
        name: 'Tiffin and Bag',
        description: 'Kids Lunchboxes & School Bags',
        handler: async function (response) {
          try {
            // Verify payment on backend FIRST (safety net if webhook fails)
            await api.verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              order_id: orderIdForSuccess,
            });
          } catch (e) {
            console.log('Payment verification call error (webhook will handle):', e);
          }

          // Track Facebook Pixel Purchase event
          if (window.fbq) {
            window.fbq('track', 'Purchase', {
              value: result.amount / 100,
              currency: 'INR',
            });
          }
          
          // Track purchase in our analytics
          try {
            await trackPurchase(orderIdForSuccess, result.amount / 100);
          } catch (e) {
            console.log('Analytics tracking error:', e);
          }
          
          // Clear cart and navigate to success page
          localStorage.removeItem('tiffin_cart');
          window.location.href = `/order-success?order_id=${orderIdForSuccess}&method=online&total=${result.amount / 100}`;
        },
        prefill: {
          name: address.name,
          contact: address.phone,
          email: address.email,
        },
        theme: {
          color: '#1DB4D6',
        },
        modal: {
          ondismiss: function() {
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error('Payment failed. Please try again.');
      });
      razorpay.open();
    } catch (error) {
      console.error('Order creation failed:', error);
      toast.error('Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if cart is empty or not loaded yet (redirect happens in useEffect)
  if (!isLoaded || cart.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen py-6 px-4 bg-gray-50" data-testid="checkout-page">
      <div className="container mx-auto max-w-4xl">
        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between max-w-md mx-auto">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">✓</div>
              <span className="text-xs mt-1 text-green-600">Cart</span>
            </div>
            <div className="flex-1 h-1 bg-green-500 mx-2"></div>
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
              <span className="text-xs mt-1 font-medium">Checkout</span>
            </div>
            <div className="flex-1 h-1 bg-gray-300 mx-2"></div>
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-gray-300 text-gray-500 rounded-full flex items-center justify-center text-sm font-bold">3</div>
              <span className="text-xs mt-1 text-gray-500">Complete</span>
            </div>
          </div>
        </div>

        <h1 className="text-2xl font-bold mb-6">Secure Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Checkout Form */}
          <div className="lg:col-span-2 space-y-5">
            {/* Login Section */}
            <Card className="p-5">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs">1</span>
                Contact Information
              </h2>
              {user ? (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 p-4 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center">✓</div>
                    <div>
                      <p className="font-medium">{user.phone}</p>
                      <p className="text-sm text-green-600">Verified via WhatsApp</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowLogin(true)}>
                    Change
                  </Button>
                </div>
              ) : (
                <Button
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => setShowLogin(true)}
                  data-testid="checkout-login-button"
                >
                  Login with WhatsApp
                </Button>
              )}
            </Card>

            {/* Delivery Address */}
            <Card className="p-5">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs">2</span>
                Delivery Address
              </h2>
              <form className="space-y-3">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={address.name}
                    onChange={(e) => setAddress({ ...address, name: e.target.value })}
                    placeholder="Enter your full name"
                    data-testid="address-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={address.phone}
                      onChange={(e) => {
                        setAddress({ ...address, phone: e.target.value });
                        // Reset COD OTP if phone changes
                        if (codOtpStep === 'verify') {
                          setCodOtpStep('idle');
                          setCodOtp('');
                        }
                      }}
                      placeholder="10-digit mobile number"
                      data-testid="address-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email (optional)</Label>
                    <Input
                      id="email"
                      type="email"
                      value={address.email}
                      onChange={(e) => setAddress({ ...address, email: e.target.value })}
                      placeholder="For order updates"
                      data-testid="address-email"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="line1">Flat / House No / Building *</Label>
                  <Input
                    id="line1"
                    value={address.line1}
                    onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                    placeholder="Flat no., House no., Building name"
                    data-testid="address-line1"
                  />
                </div>
                <div>
                  <Label htmlFor="line2">Area / Street / Colony *</Label>
                  <Input
                    id="line2"
                    value={address.line2}
                    onChange={(e) => setAddress({ ...address, line2: e.target.value })}
                    placeholder="Street name, Area, Colony"
                    data-testid="address-line2"
                  />
                </div>
                <div>
                  <Label htmlFor="landmark">Landmark (optional)</Label>
                  <Input
                    id="landmark"
                    value={address.landmark}
                    onChange={(e) => setAddress({ ...address, landmark: e.target.value })}
                    placeholder="Near temple, Behind mall, etc."
                    data-testid="address-landmark"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={address.city}
                      onChange={(e) => setAddress({ ...address, city: e.target.value })}
                      placeholder="City"
                      data-testid="address-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={address.state}
                      onChange={(e) => setAddress({ ...address, state: e.target.value })}
                      placeholder="State"
                      data-testid="address-state"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="pincode">Pincode *</Label>
                  <Input
                    id="pincode"
                    value={address.pincode}
                    onChange={(e) => setAddress({ ...address, pincode: e.target.value })}
                    placeholder="6-digit pincode"
                    maxLength={6}
                    data-testid="address-pincode"
                  />
                </div>
              </form>
            </Card>

            {/* Payment Method Selection */}
            <Card className="p-5">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs">3</span>
                Payment Method
              </h2>
              <RadioGroup value={paymentMethod} onValueChange={(val) => {
                setPaymentMethod(val);
                setCodOtpStep('idle');
                setCodOtp('');
              }}>
                <div className="space-y-3">
                  {/* Online Payment Option */}
                  <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors ${paymentMethod === 'online' ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                    <RadioGroupItem value="online" id="online" data-testid="payment-online" />
                    <Label htmlFor="online" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-primary" />
                        <span className="font-medium">Pay Online</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        UPI, Cards, Net Banking via Razorpay
                      </p>
                    </Label>
                  </div>

                  {/* COD Option */}
                  <div className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-colors ${paymentMethod === 'cod' ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                    <RadioGroupItem value="cod" id="cod" className="mt-1" data-testid="payment-cod" />
                    <Label htmlFor="cod" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Banknote className="w-5 h-5 text-green-600" />
                        <span className="font-medium">Cash on Delivery</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Pay when your order arrives
                      </p>
                    </Label>
                  </div>
                </div>
              </RadioGroup>

              {/* COD OTP Verification */}
              {paymentMethod === 'cod' && (
                <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  {isUserVerified ? (
                    // User already verified via WhatsApp - no OTP needed
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-green-800">Auto-verified via WhatsApp</p>
                        <p className="text-sm text-green-600">
                          No additional OTP needed - you're already verified!
                        </p>
                      </div>
                    </div>
                  ) : (
                    // User not logged in - require OTP
                    <>
                      <div className="flex items-start gap-2 mb-3">
                        <ShieldCheck className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">WhatsApp OTP Verification Required</p>
                          <p className="text-sm text-amber-700">
                            To prevent fake orders, please verify your phone number via WhatsApp OTP.
                          </p>
                        </div>
                      </div>

                      {codOtpStep === 'idle' && (
                        <Button 
                          onClick={handleSendCodOtp} 
                          disabled={codOtpSending || !address.phone}
                          className="w-full"
                          variant="outline"
                          data-testid="cod-send-otp-button"
                        >
                          {codOtpSending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending OTP...
                            </>
                          ) : (
                            'Send OTP to WhatsApp'
                          )}
                        </Button>
                      )}

                      {codOtpStep === 'verify' && (
                        <div className="space-y-3">
                          <Label>Enter 6-digit OTP sent to {address.phone}</Label>
                          <div className="flex justify-center">
                            <InputOTP
                              maxLength={6}
                              value={codOtp}
                              onChange={setCodOtp}
                              data-testid="cod-otp-input"
                            >
                              <InputOTPGroup>
                                <InputOTPSlot index={0} />
                                <InputOTPSlot index={1} />
                                <InputOTPSlot index={2} />
                                <InputOTPSlot index={3} />
                                <InputOTPSlot index={4} />
                                <InputOTPSlot index={5} />
                              </InputOTPGroup>
                            </InputOTP>
                          </div>
                          {devOtp && (
                            <p className="text-xs text-center text-muted-foreground">
                              Development OTP: {devOtp}
                            </p>
                          )}
                          {codOtp.length === 6 && (
                            <div className="flex items-center justify-center gap-2 text-green-600">
                              <ShieldCheck className="w-4 h-4" />
                              <span className="text-sm font-medium">OTP entered - Ready to place order</span>
                            </div>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setCodOtpStep('idle');
                              setCodOtp('');
                            }}
                            className="w-full"
                          >
                            Change phone number / Resend OTP
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="p-5 sticky top-4 border-2 border-primary/20">
              <h2 className="text-lg font-bold mb-4">Order Summary</h2>
              
              {/* Cart Items with Images */}
              <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                {cart.map(item => (
                  <div key={item.id} className="flex items-center gap-3">
                    <img 
                      src={item.image_urls?.[0] || 'https://via.placeholder.com/40'} 
                      alt={item.title}
                      className="w-10 h-10 rounded object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <span className="text-sm font-semibold">₹{(item.price * item.quantity).toFixed(0)}</span>
                  </div>
                ))}
              </div>
              
              {/* Coupon Section */}
              <div className="mb-4">
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200">
                    <div>
                      <p className="text-sm font-medium text-green-800">{appliedCoupon.code}</p>
                      <p className="text-xs text-green-600">You save ₹{discountAmount.toFixed(2)}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={removeCoupon} className="text-red-500 hover:text-red-700">
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1"
                      data-testid="coupon-input"
                    />
                    <Button 
                      variant="outline" 
                      onClick={handleApplyCoupon}
                      disabled={couponLoading}
                      data-testid="apply-coupon-btn"
                    >
                      {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                )}
              </div>
              
              <Separator className="my-4" />
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span className="font-semibold">₹{cartTotal.toFixed(2)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-₹{discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span>Delivery</span>
                  <FreeDeliveryPill />
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between text-lg font-bold" data-testid="checkout-total">
                  <span>Total</span>
                  <span>₹{finalTotal.toFixed(0)}</span>
                </div>
              </div>
              
              {/* Place Order Button */}
              <Button
                size="lg"
                className="w-full mt-5 rounded-xl h-12 text-base bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600"
                onClick={handleSubmit}
                disabled={loading || !user || (paymentMethod === 'cod' && !isUserVerified && codOtp.length !== 6)}
                data-testid="place-order-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : paymentMethod === 'cod' ? (
                  `Place COD Order - ₹${finalTotal.toFixed(0)}`
                ) : (
                  `Pay ₹${finalTotal.toFixed(0)}`
                )}
              </Button>
              
              {/* Trust indicators */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-2">
                  <Lock className="w-3 h-3" />
                  <span>Secure checkout</span>
                </div>
                <div className="flex justify-center gap-2">
                  <img src="https://razorpay.com/favicon.png" alt="Razorpay" className="h-4 opacity-50" />
                </div>
              </div>
            </Card>
            
            {/* Quick Help */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                Need help? <a href="https://wa.me/918062180632" className="text-green-600 hover:underline">WhatsApp us</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <WhatsAppOtpLogin
        open={showLogin}
        onOpenChange={setShowLogin}
        onSuccess={handleLoginSuccess}
      />
    </div>
  );
}

export default CheckoutPage;
